// src/components/MalawiMapOnly.tsx
import React, { useEffect, useMemo, useState } from 'react';
import Map, { Source, Layer } from 'react-map-gl/maplibre';
import type { LayerProps } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { StyleSpecification } from 'maplibre-gl';

interface MalawiMapOnlyProps {
  /** API response containing incident data */
  incidentData?: any;
  /** Component height in px */
  height?: number;
  /** Callback when a district is clicked */
  onDistrictClick?: (districtName: string) => void;
}

// 🎨 Dynamic incident-based color scale algorithm
const calculateDynamicRanges = (incidentCounts: number[]) => {
  const nonZeroCounts = incidentCounts.filter(count => count > 0).sort((a, b) => a - b);

  if (nonZeroCounts.length === 0) {
    return {
      veryLow: { min: 1, max: 2 },
      low: { min: 3, max: 4 },
      medium: { min: 5, max: 9 },
      high: { min: 10, max: 19 },
      critical: { min: 20, max: Infinity }
    };
  }

  const min = nonZeroCounts[0];
  const max = nonZeroCounts[nonZeroCounts.length - 1];
  const range = max - min;

  // Calculate quintiles (5 equal groups)
  const quintileSize = range / 5;

  return {
    veryLow: {
      min: min,
      max: Math.ceil(min + quintileSize)
    },
    low: {
      min: Math.ceil(min + quintileSize) + 1,
      max: Math.ceil(min + quintileSize * 2)
    },
    medium: {
      min: Math.ceil(min + quintileSize * 2) + 1,
      max: Math.ceil(min + quintileSize * 3)
    },
    high: {
      min: Math.ceil(min + quintileSize * 3) + 1,
      max: Math.ceil(min + quintileSize * 4)
    },
    critical: {
      min: Math.ceil(min + quintileSize * 4) + 1,
      max: max
    }
  };
};

// Dynamic color function
const getColor = (count: number, ranges: any): string => {
  if (count === 0) return '#f3f4f6';     // No Incidents - Gray-100
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return '#dbeafe';   // Very Low - Blue-100
  if (count >= ranges.low.min && count <= ranges.low.max) return '#bfdbfe';   // Low - Blue-200
  if (count >= ranges.medium.min && count <= ranges.medium.max) return '#fbbf24';   // Medium - Amber-400
  if (count >= ranges.high.min && count <= ranges.high.max) return '#f59e0b'; // High - Amber-500
  return '#dc2626'; // Critical - Red-600
};

// Get color category for styling and legend
const getColorCategory = (count: number, ranges: any): string => {
  if (count === 0) return 'none';
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return 'very-low';
  if (count >= ranges.low.min && count <= ranges.low.max) return 'low';
  if (count >= ranges.medium.min && count <= ranges.medium.max) return 'medium';
  if (count >= ranges.high.min && count <= ranges.high.max) return 'high';
  return 'critical';
};

// Get category label for display
const getCategoryLabel = (count: number, ranges: any): string => {
  if (count === 0) return 'No Incidents';
  if (count >= ranges.veryLow.min && count <= ranges.veryLow.max) return `Very Low (${ranges.veryLow.min}-${ranges.veryLow.max})`;
  if (count >= ranges.low.min && count <= ranges.low.max) return `Low (${ranges.low.min}-${ranges.low.max})`;
  if (count >= ranges.medium.min && count <= ranges.medium.max) return `Medium (${ranges.medium.min}-${ranges.medium.max})`;
  if (count >= ranges.high.min && count <= ranges.high.max) return `High (${ranges.high.min}-${ranges.high.max})`;
  return `Critical (${ranges.critical.min}+)`;
};

// Normalize keys: collapse spaces, trim, lowercase
const norm = (s?: string | null) =>
  s?.toString().replace(/\s+/g, ' ').trim().toLowerCase() ?? '';

const MalawiMapOnly: React.FC<MalawiMapOnlyProps> = ({
  incidentData = {},
  height = 700,
  onDistrictClick,
}) => {
  const [geoJson, setGeoJson] = useState<any>(null);
  const [hovered, setHovered] = useState<{ name: string; count: number; x: number; y: number; category: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [dynamicRanges, setDynamicRanges] = useState<any>(null);

  // 🔍 Debug: Log the incident data received from parent component
  useEffect(() => {
    console.log('📊 MalawiMapOnly received incidentData:', incidentData);
    console.log('📊 incidentData type:', typeof incidentData);

    // Support multiple nesting variants:
    const body = incidentData?.data ?? incidentData ?? {};
    const mapInc = body.map_incidents || body.data?.map_incidents || incidentData?.data?.data?.map_incidents;
    const incidentsArray = mapInc?.incidents_by_location;
    console.log('📊 incidents_by_location array:', incidentsArray);

    if (Array.isArray(incidentsArray)) {
      console.log('📊 District names in data:');
      incidentsArray.forEach((district: any, index: number) => {
        console.log(`  ${index}: "${district.district_name}" - ${district.incident_count} incidents`);
      });
    }
  }, [incidentData]);

  // ✅ Extract and transform incident data from API response
  const districtIncidentMap = useMemo(() => {
    const body = incidentData?.data ?? incidentData ?? {};
    const mapInc = body.map_incidents || body.data?.map_incidents || incidentData?.data?.data?.map_incidents;
    const incidentsArray = mapInc?.incidents_by_location;

    if (!Array.isArray(incidentsArray)) {
      console.log('📊 No incidents_by_location array found');
      return {};
    }

    // Create a map of normalized district name -> incident count
    const map: Record<string, number> = {};

    incidentsArray.forEach((district: any) => {
      const districtName = district.district_name;
      const incidentCount = district.incident_count || 0;

      if (districtName) {
        const normalizedName = norm(districtName);
        map[normalizedName] = incidentCount;
        console.log(`📊 Mapped: "${districtName}" -> normalized: "${normalizedName}" -> count: ${incidentCount}`);
      }
    });

    console.log('📊 Final district incident map:', map);
    return map;
  }, [incidentData]);

  // ✅ Load districts GeoJSON only
  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await fetch('/districts_malawi.geojson');
        const data = await res.json();
        if (!data?.features) {
          console.warn('❌ GeoJSON missing features:', data);
          return;
        }
        setGeoJson(data);
      } catch (e) {
        console.error('❌ Failed to load /districts_malawi.geojson:', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  // ✅ Build normalized lookup for incident data - Now using the transformed district map
  const normalizedIncidentData = useMemo(
    () => districtIncidentMap,
    [districtIncidentMap]
  );

  // ✅ Enrich features with incidentCount + color (expects properties.name in GeoJSON)
  const enrichedGeoJson = useMemo(() => {
    if (!geoJson?.features) return null;

    console.log('📊 Processing GeoJSON features...');
    console.log('📊 Available normalized incident data keys:', Object.keys(normalizedIncidentData));

    // First pass: collect all incident counts to calculate dynamic ranges
    const allCounts = geoJson.features.map((f: any) => {
      const geoJsonName = f?.properties?.name;
      const key = norm(geoJsonName);
      return normalizedIncidentData[key] ?? 0;
    });

    // Calculate dynamic ranges based on actual data
    const ranges = calculateDynamicRanges(allCounts);
    setDynamicRanges(ranges);

    console.log('📊 Dynamic ranges calculated:', ranges);
    console.log('📊 Incident counts distribution:', {
      min: Math.min(...allCounts.filter((c: number) => c > 0)),
      max: Math.max(...allCounts),
      total: allCounts.reduce((sum: number, c: number) => sum + c, 0),
      districts: allCounts.length
    });

    const features = geoJson.features.map((f: any) => {
      const geoJsonName = f?.properties?.name;
      const key = norm(geoJsonName);
      const count = normalizedIncidentData[key] ?? 0;

      console.log(`📊 GeoJSON district: "${geoJsonName}" -> normalized: "${key}" -> incidents: ${count}`);

      return {
        ...f,
        properties: {
          ...f.properties,
          incidentCount: count,
          color: getColor(count, ranges),
        },
      };
    });

    // Dev hint: list the first few unmatched names
    if (process.env.NODE_ENV !== 'production') {
      const missing = features
        .filter((f: any) => (f.properties?.incidentCount ?? 0) === 0)
        .map((f: any) => f.properties?.name);
      if (missing.length) {
        console.debug(`[Map districts] Unmatched names (first 10 of ${missing.length}):`, missing.slice(0, 10));
      }

      // Show matched ones too
      const matched = features
        .filter((f: any) => (f.properties?.incidentCount ?? 0) > 0)
        .map((f: any) => ({ name: f.properties?.name, count: f.properties?.incidentCount }));
      if (matched.length) {
        console.debug(`[Map districts] Matched districts (${matched.length}):`, matched);
      }
    }

    return { ...geoJson, features };
  }, [geoJson, normalizedIncidentData]);

  // ✅ Minimal blank basemap
  const blankStyle = useMemo<StyleSpecification>(
    () => ({
      version: 8,
      name: 'blank',
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {},
      layers: [
        {
          id: 'background',
          type: 'background',
          paint: { 'background-color': '#f5f7fb' },
        },
      ],
    }),
    []
  );

  // Enhanced map layers with better styling
  const fillLayerId = 'dist-fill';

  const fillLayer: LayerProps = {
    id: fillLayerId,
    type: 'fill',
    paint: {
      'fill-color': ['get', 'color'],
      'fill-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        0.95,
        0.8
      ],
    },
  };

  const borderLayer: LayerProps = {
    id: 'dist-borders',
    type: 'line',
    paint: {
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#1f2937',
        '#6b7280'
      ],
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        2,
        1
      ],
    },
  };

  const labelLayer: LayerProps = {
    id: 'dist-labels',
    type: 'symbol',
    layout: {
      'text-field': ['coalesce', ['get', 'abbr'], ['get', 'name'], ''],
      'text-size': 11,
      'text-anchor': 'center',
      'symbol-placement': 'point',
    },
    paint: {
      'text-color': '#1f2937',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2,
    },
  };

  return (
    <div className="relative w-full overflow-hidden shadow-xl bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl" style={{ height }}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-3 border-4 border-blue-200 rounded-full border-t-blue-500 animate-spin"></div>
            <p className="text-sm font-medium text-gray-600">Loading Malawi Districts...</p>
          </div>
        </div>
      )}

      {/* Enhanced Map Container */}
      <div className="relative w-full h-full">
        <Map
          initialViewState={{
            latitude: -13.2543,
            longitude: 34.3015,
            zoom: 5.2,
            pitch: 0,
            bearing: 0
          }}
          style={{ width: '100%', height: '100%' }}
          mapStyle={blankStyle}
          maxZoom={7.0}
          minZoom={5.2}
          interactiveLayerIds={[fillLayerId]}
          onMouseMove={(e) => {
            const feature: any = e.features?.[0];
            if (feature?.properties && dynamicRanges) {
              const count = feature.properties.incidentCount ?? 0;
              setHovered({
                name: feature.properties.name,
                count: count,
                category: getColorCategory(count, dynamicRanges),
                x: e.point.x,
                y: e.point.y,
              });
            } else {
              setHovered(null);
            }
          }}
          onMouseLeave={() => setHovered(null)}
          onClick={(e) => {
            const feature: any = e.features?.[0];
            if (feature?.properties) {
              const districtName = feature.properties.name;
              setSelectedDistrict(districtName);
              // Call the callback if provided
              if (onDistrictClick) {
                onDistrictClick(districtName);
              }
            }
          }}
        >
          {enrichedGeoJson?.features?.length > 0 && (
            <Source id="malawi-districts" type="geojson" data={enrichedGeoJson}>
              <Layer {...fillLayer} />
              <Layer {...borderLayer} />
              <Layer {...labelLayer} />
            </Source>
          )}
        </Map>
      </div>

      {/* 🎨 Enhanced Modern Legend - Compact Version */}
      <div className="absolute z-20 bottom-4 left-4">
        <div className="overflow-hidden bg-white border border-gray-200 shadow-xl rounded-xl backdrop-blur-sm bg-opacity-95">
          {/* Legend Header */}
          <div className="px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600">
            <div className="flex items-center space-x-1.5">
              <div className="p-1 rounded-md bg-white/20 backdrop-blur-sm">
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xs font-bold text-white">Incident Density</h3>
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
            </div>
          </div>

          {/* Dynamic Legend Items */}
          <div className="p-2 space-y-1.5">
            {dynamicRanges ? (
              <>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 bg-gray-100 border border-gray-300 rounded shadow-sm"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">No Incidents</span>
                  <span className="ml-auto font-mono text-xs text-gray-400">0</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-blue-100 border-blue-200"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Very Low</span>
                  <span className="ml-auto font-mono text-xs text-blue-600">{dynamicRanges.veryLow.min}-{dynamicRanges.veryLow.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-blue-200 border-blue-300"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Low</span>
                  <span className="ml-auto font-mono text-xs text-blue-700">{dynamicRanges.low.min}-{dynamicRanges.low.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-amber-400 border-amber-500"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Medium</span>
                  <span className="ml-auto font-mono text-xs text-amber-700">{dynamicRanges.medium.min}-{dynamicRanges.medium.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 border rounded shadow-sm bg-amber-500 border-amber-600"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">High</span>
                  <span className="ml-auto font-mono text-xs text-amber-800">{dynamicRanges.high.min}-{dynamicRanges.high.max}</span>
                </div>
                <div className="flex items-center space-x-2 group">
                  <div className="w-3 h-3 bg-red-600 border border-red-700 rounded shadow-sm"></div>
                  <span className="text-xs font-medium text-gray-700 transition-colors group-hover:text-gray-900">Critical</span>
                  <span className="ml-auto font-mono text-xs text-red-700">{dynamicRanges.critical.min}+</span>
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-500 text-center py-2">
                Calculating ranges...
              </div>
            )}
          </div>
        </div>
      </div>      {/* 🎯 Enhanced Tooltip */}
      {hovered && (
        <div
          className="absolute z-30 transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{
            left: hovered.x,
            top: hovered.y - 12,
          }}
        >
          <div className="relative">
            {/* Tooltip Arrow */}
            <div className="absolute transform -translate-x-1/2 top-full left-1/2">
              <div className="w-0 h-0 border-t-4 border-l-4 border-r-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
            </div>

            {/* Tooltip Content */}
            <div className="px-4 py-3 bg-gray-900 shadow-2xl rounded-xl backdrop-blur-sm min-w-max">
              <div className="mb-1 text-sm font-bold text-white">
                {hovered.name}
              </div>
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full shadow-sm"
                  style={{ backgroundColor: dynamicRanges ? getColor(hovered.count, dynamicRanges) : '#f3f4f6' }}
                ></div>
                <span className="text-xs text-gray-300">
                  <span className="font-semibold text-white">{hovered.count}</span> incident{hovered.count !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Enhanced Severity Badge with dynamic ranges */}
              {dynamicRanges && (
                <div className="mt-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    hovered.category === 'critical' ? 'bg-red-600 text-red-100' :
                    hovered.category === 'high' ? 'bg-amber-500 text-amber-100' :
                    hovered.category === 'medium' ? 'bg-amber-400 text-amber-900' :
                    hovered.category === 'low' ? 'bg-blue-200 text-blue-900' :
                    hovered.category === 'very-low' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {getCategoryLabel(hovered.count, dynamicRanges)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 📍 Selected District Info Panel */}

    </div>
  );
};

export default MalawiMapOnly;
